<script setup>
import {computed, onBeforeUnmount, onMounted, ref, watch} from 'vue';

const props = defineProps({
    categories: {
        type: Array,
        default: () => [],
    },

    promoBaseUrl: {
        type: String,
        required: true,
    },

    categoriesTreeUrl: {
        type: String,
        required: true,
    },

    title: {
        type: String,
        default: 'Kategorie',
    },

    viewAllText: {
        type: String,
        default: 'Zobacz wszystko',
    },

    emptySubcategoriesText: {
        type: String,
        default: 'Brak podkategorii w tej sekcji.',
    },
});

const selectedCategory = ref(null);
const panelOpen = ref(false);
const loading = ref(false);

const wrapperRef = ref(null);
const panelBodyRef = ref(null);
const carouselColRef = ref(null);

const filteredCategories = computed(() => {
    return props.categories.filter(
        c => (c.name || '').toLowerCase() !== 'outlet'
    );
});

const categoryChildren = computed(() => {
    if (!selectedCategory.value) {
        return [];
    }

    return (
        selectedCategory.value.children ||
        selectedCategory.value.children_data ||
        selectedCategory.value.items ||
        []
    );
});

const lockScroll = () => {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.paddingRight = scrollbarWidth + 'px';
    document.body.style.overflow = 'hidden';
};

const unlockScroll = () => {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
};

const hoverCategory = async (item) => {
    const wasOpen = panelOpen.value;
    selectedCategory.value = item;
    panelOpen.value = true;
    if (!wasOpen) {
        lockScroll();
    }

    const hasChildren =
        item.children?.length ||
        item.children_data?.length ||
        item.items?.length;

    if (hasChildren) {
        return;
    }

    loading.value = true;

    try {
        const response = await fetch(
            `${props.categoriesTreeUrl}?parent_id=${item.id}`
        );

        const json = await response.json();

        item.children = json.data || [];
    } catch (e) {
        console.error(e);
    } finally {
        loading.value = false;
    }
};

const goToCategory = (slug) => {
    window.location.href = '/' + slug;
};

const closePanel = () => {
    panelOpen.value = false;
    unlockScroll();
};

const scrollSubcategories = (direction) => {
    if (!panelBodyRef.value) {
        return;
    }

    const scrollAmount = panelBodyRef.value.clientWidth * 0.8;

    panelBodyRef.value.scrollBy({
        left: direction * scrollAmount,
        behavior: 'smooth',
    });
};

let cachedWrapperRect = null;
let mouseMoveRaf = false;

const updateWrapperRect = () => {
    if (wrapperRef.value && panelOpen.value) {
        cachedWrapperRect = wrapperRef.value.getBoundingClientRect();
    } else {
        cachedWrapperRect = null;
    }
};

const handleOutsideClick = (event) => {
    if (!panelOpen.value || !wrapperRef.value) {
        return;
    }

    if (!wrapperRef.value.contains(event.target)) {
        closePanel();
    }
};

const handleMouseMove = (e) => {
    if (!panelOpen.value || !wrapperRef.value) {
        return;
    }

    if (mouseMoveRaf) return;
    mouseMoveRaf = true;

    requestAnimationFrame(() => {
        mouseMoveRaf = false;
        if (!panelOpen.value || !wrapperRef.value) return;

        if (!cachedWrapperRect) {
            updateWrapperRect();
        }

        const rect = cachedWrapperRect;
        if (!rect) return;

        const padding = 500;
        const isOutside =
            e.clientX < rect.left - padding ||
            e.clientX > rect.right + padding ||
            e.clientY < rect.top ||
            e.clientY > rect.bottom;

        if (isOutside) {
            closePanel();
        }
    });
};

const handleResize = () => {
    if (panelOpen.value) {
        updateWrapperRect();
    }
};

watch(panelOpen, (isOpen) => {
    if (isOpen) {
        requestAnimationFrame(updateWrapperRect);
    } else {
        cachedWrapperRect = null;
    }
});

onMounted(() => {
    document.addEventListener('click', handleOutsideClick, true);
    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
});

onBeforeUnmount(() => {
    document.removeEventListener('click', handleOutsideClick, true);
    document.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('resize', handleResize);
    unlockScroll();
});
</script>

<template>
    <div
        ref="wrapperRef"
        class="max-w-[1400px] mb-0 mx-auto lg:px-3 lg:py-6 !pb-0 flex flex-col lg:flex-row gap-3 items-stretch categories-section"
    >
        <!-- LEFT -->
        <div class="order-2 flex flex-col">
            <div class="lg:hidden">
                <section class="section max-w-[1400px] mx-auto lg:px-3">
                    <div class="header-container">
                        <h2 class="uppercase flex items-center gap-2">
                            <img src="../assets/e4u_sygnet_green.svg" alt="Sygnet" class="w-[55px] h-[35px] object-contain">
                            {{ title || 'Kategorie' }}
                        </h2>
                    </div>
                </section>
            </div>

            <div
                id="category"
                class="relative flex-grow flex flex-col justify-between"
            >
                <div
                    class="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible snap-x snap-mandatory pb-6 hide-scrollbar max-lg:gap-3 max-lg:px-2"
                >
                    <button
                        v-for="item in filteredCategories"
                        :key="item.id"
                        type="button"
                        :class="[
                            'w-full flex max-lg:flex-col items-center gap-2 lg:pr-4 px-1 text-left group transition-all border-2 border-transparent max-lg:w-auto max-lg:min-w-[90px] max-lg:max-w-[90px] max-lg:flex-shrink-0 max-lg:p-0 max-lg:gap-2 max-lg:text-center max-lg:bg-transparent max-lg:border-0',
                            {
                                'is-active font-bold': selectedCategory?.id === item.id
                            }
                        ]"
                        @mouseenter="hoverCategory(item)"
                        @click="goToCategory(item.slug)"
                    >
                        <div
                            v-if="item.logo_url || item.logo_path"
                            class="relative flex-shrink-0 flex items-center justify-center w-[38px] h-[38px] max-lg:w-[75px] max-lg:h-[75px]"
                        >
                            <img
                                :src="item.logo_url || (item.logo_path ? (item.logo_path.startsWith('/') ? item.logo_path : '/storage/' + item.logo_path) : '')"
                                alt=""
                                aria-hidden="true"
                                class="w-[38px] h-[38px] max-lg:w-[75px] max-lg:h-[75px] object-contain"
                            >
                        </div>

                        <span
                            class="lg:w-48 text-gray-900 transition-colors max-lg:text-center max-lg:text-xs text-[16px] group-hover:font-bold group-hover:text-primary max-lg:leading-tight"
                        >
                            {{ item.name }}
                        </span>

                        <img
                            class="w-3 h-3 text-gray-400 group-hover:text-primary max-lg:hidden transition-all flex-shrink-0"
                            :class="{
                                'translate-x-2 text-primary': selectedCategory?.id === item.id
                            }"
                            src="../assets/category-arrow.svg"
                            alt=""
                        >
                    </button>
                </div>
            </div>
        </div>

        <!-- RIGHT -->
        <div
            ref="carouselColRef"
            class="order-1 lg:order-2 w-full relative overflow-visible flex flex-col self-stretch"
        >
            <!-- ACTIVE BAR -->
            <div
                v-if="panelOpen && selectedCategory"
                class="absolute z-[4] left-0 w-[6px] bg-primary transition-all duration-200"
                :style="{
                    top: `${filteredCategories.findIndex(x => x.id === selectedCategory.id) * 48}px`,
                    height: '48px'
                }"
            />

            <!-- PANEL -->
            <div
                v-if="panelOpen"
                class="absolute top-0 w-full text-white flex flex-col sm:rounded-md"
                style="height: 630px; background-color: rgba(36 36 35 / 0.83); z-index: 3; box-shadow: -1px 7px 10.3px rgba(0, 0, 0, 0.57); backdrop-filter: blur(2px);"
            >
                <!-- HEADER -->
                <div class="flex flex-col items-start py-2 px-6 flex-shrink-0">
                    <div class="px-4 pt-2 text-xl uppercase tracking-wider">
                        {{ selectedCategory?.name }}
                    </div>

                    <a
                        :href="`${promoBaseUrl}?promotions=1&category_id=${selectedCategory?.id}`"
                        class="px-4 text-sm hover:text-primary transition-colors"
                    >
                        {{ viewAllText || 'Zobacz wszystko' }}
                    </a>
                </div>

                <!-- BODY -->
                <div class="relative flex-grow overflow-hidden flex flex-col min-h-0 px-6">
                    <!-- LEFT -->
                    <button
                        type="button"
                        @click="scrollSubcategories(-1)"
                        class="absolute left-2 z-20 bg-black/50 hover:bg-black/80 text-white rounded-full p-2 hidden items-center justify-center transition-all"
                    >
                        <svg
                            class="w-8 h-8"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                    </button>

                    <!-- CONTENT -->
                    <div
                        ref="panelBodyRef"
                        class="flex-grow grid grid-cols-5 gap-x-6 gap-y-2 overflow-y-auto pt-4 pb-3 px-10 min-h-0 custom-scrollbar"
                    >
                        <!-- LOADING -->
                        <div
                            v-if="loading"
                            class="w-full col-span-5 flex items-center justify-center py-10"
                        >
                            <div
                                class="animate-spin rounded-full h-10 w-10 border-b-2 border-white"
                            />
                        </div>

                        <!-- EMPTY -->
                        <div
                            v-else-if="!categoryChildren.length"
                            class="w-full col-span-5 text-center text-lg text-white font-light"
                        >
                            {{ emptySubcategoriesText || 'Brak podkategorii w tej sekcji.' }}
                        </div>

                        <!-- ITEMS -->
                        <a
                            v-for="child in categoryChildren"
                            v-else
                            :key="child.id"
                            :href="child.slug ? '/' + child.slug : '#'"
                            class="w-full group block text-center"
                        >
                            <div
                                class="aspect-square rounded-lg shadow-2xl flex items-center justify-center transition-all duration-300 group-hover:scale-105 overflow-hidden"
                            >
                                <img
                                    v-if="child.logo_url || child.image_url || child.logo_path"
                                    :src="child.logo_url || child.image_url || (child.logo_path ? (child.logo_path.startsWith('/') ? child.logo_path : '/storage/' + child.logo_path) : '')"
                                    :alt="child.name"
                                    class="max-w-[100%] max-h-[100%] object-contain"
                                >

                                <div
                                    v-else
                                    class="text-4xl text-gray-300 font-bold"
                                >
                                    {{ child.name?.charAt(0)?.toUpperCase() }}
                                </div>
                            </div>

                            <div
                                class="mt-2 text-sm md:text-base font-light text-white group-hover:text-primary transition-colors leading-tight min-h-[52px] flex items-start justify-center"
                            >
                                {{ child.name }}
                            </div>
                        </a>
                    </div>

                    <!-- RIGHT -->
                    <button
                        type="button"
                        @click="scrollSubcategories(1)"
                        class="absolute right-2 z-10 bg-black/50 hover:bg-black/80 text-white rounded-full p-2 hidden items-center justify-center transition-all"
                    >
                        <svg
                            class="w-8 h-8"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M9 5l7 7-7 7"
                            />
                        </svg>
                    </button>
                </div>

                <!-- FOOTER -->
                <div class="py-2 px-6 flex justify-end flex-shrink-0">
                </div>
            </div>

            <!-- SLOT -->
            <div class="overflow-hidden sm:rounded-md">
                <slot />
            </div>
        </div>
    </div>
</template>
